import type { Cell } from '@omen-quest/grid-map';
import type { DungeonMapData } from '@/lib/db/dungeon-types';

export function buildGridFromMapData(mapData: DungeonMapData): Cell[][] {
  const { gridWidth, gridHeight, rooms, corridors } = mapData;

  // Initialize all cells as walls
  const cells: Cell[][] = [];
  for (let y = 0; y < gridHeight; y++) {
    cells[y] = [];
    for (let x = 0; x < gridWidth; x++) {
      cells[y][x] = {
        x,
        y,
        terrain: 'wall',
        walls: { N: true, E: true, S: true, W: true },
      };
    }
  }

  // Carve room rectangles as floor
  for (const room of rooms) {
    for (let ry = room.y; ry < room.y + room.height && ry < gridHeight; ry++) {
      for (let rx = room.x; rx < room.x + room.width && rx < gridWidth; rx++) {
        cells[ry][rx].terrain = 'floor';
      }
    }
  }

  // Carve corridor paths as floor
  for (const corridor of corridors) {
    for (const cell of corridor.cells) {
      if (cell.y >= 0 && cell.y < gridHeight && cell.x >= 0 && cell.x < gridWidth) {
        cells[cell.y][cell.x].terrain = 'floor';
      }
    }

    // Place doors at corridor-room boundaries
    if (corridor.doorType) {
      const doorCells = findDoorCells(corridor, rooms);
      for (const dc of doorCells) {
        if (dc.y >= 0 && dc.y < gridHeight && dc.x >= 0 && dc.x < gridWidth) {
          cells[dc.y][dc.x].terrain = 'door';
        }
      }
    }
  }

  // Compute walls: a floor cell has a wall on any side adjacent to a non-floor cell
  for (let y = 0; y < gridHeight; y++) {
    for (let x = 0; x < gridWidth; x++) {
      if (cells[y][x].terrain === 'floor' || cells[y][x].terrain === 'door') {
        cells[y][x].walls = {
          N: y === 0 || cells[y - 1][x].terrain === 'wall',
          S: y === gridHeight - 1 || cells[y + 1][x].terrain === 'wall',
          W: x === 0 || cells[y][x - 1].terrain === 'wall',
          E: x === gridWidth - 1 || cells[y][x + 1].terrain === 'wall',
        };
      }
    }
  }

  return cells;
}

function findDoorCells(
  corridor: DungeonMapData['corridors'][number],
  rooms: DungeonMapData['rooms']
): { x: number; y: number }[] {
  if (corridor.cells.length === 0) return [];

  const doorCells: { x: number; y: number }[] = [];

  const firstCell = corridor.cells[0];
  const lastCell = corridor.cells[corridor.cells.length - 1];

  for (const cell of [firstCell, lastCell]) {
    for (const room of rooms) {
      const isAdjacentToRoom =
        cell.x >= room.x - 1 &&
        cell.x <= room.x + room.width &&
        cell.y >= room.y - 1 &&
        cell.y <= room.y + room.height;
      const isInsideRoom =
        cell.x >= room.x &&
        cell.x < room.x + room.width &&
        cell.y >= room.y &&
        cell.y < room.y + room.height;

      if (isAdjacentToRoom && !isInsideRoom) {
        doorCells.push(cell);
        break;
      }
    }
  }

  return doorCells;
}
